import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { 
  Search, Filter, Calendar, User, Hash, ThumbsUp, ThumbsDown, 
  Eye, Check, X, RefreshCw, AlertCircle, TrendingUp, Download 
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import './WebScraping.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WebScraping = ({ onAdmitResult }) => {
  const { canEdit } = useAuth();
  const userCanEdit = React.useMemo(() => canEdit(), []); // Memoize to prevent re-renders
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cuentasSociales, setCuentasSociales] = useState([]);
  const [redesSociales, setRedesSociales] = useState([]);
  const [showAdmitModal, setShowAdmitModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedResults, setSelectedResults] = useState([]); // Para selección múltiple
  
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    network: 'X',
    author: '',
    person: '',
    sentiment: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchResults();
    fetchCatalogos();
  }, []);

  const fetchCatalogos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [cuentasRes, redesRes] = await Promise.all([
        axios.get(`${API}/catalogs/cuentas_sociales`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/catalogs/networks`, { headers }).catch(() => ({ data: [] }))
      ]);
      setCuentasSociales((cuentasRes.data || []).filter(c => c.is_active !== false));
      setRedesSociales((redesRes.data || []).filter(r => r.is_active !== false));
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/catalogs/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const fetchResults = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/scraping/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data || []);
    } catch (error) {
      console.error('Error al cargar resultados:', error);
      toast.error('Error al cargar resultados');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    // Validate required fields
    if (!filters.date_from || !filters.date_to) {
      toast.error('Las fechas son obligatorias');
      return;
    }

    // Validate author format if provided (@username)
    if (filters.author && filters.author.trim() !== '' && !filters.author.startsWith('@')) {
      toast.error('La cuenta debe comenzar con @ (ejemplo: @armandocastGT)');
      return;
    }

    // Validate date range (max 5 days)
    const dateFrom = new Date(filters.date_from);
    const dateTo = new Date(filters.date_to);
    const diffDays = Math.ceil((dateTo - dateFrom) / (1000 * 60 * 60 * 24));
    
    if (diffDays > 5) {
      toast.error('El rango de fechas no puede ser mayor a 5 días');
      return;
    }

    if (diffDays < 0) {
      toast.error('La fecha inicial debe ser menor a la fecha final');
      return;
    }

    setSearching(true);
    setResults([]); // Clear previous results
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/scraping/search`, filters, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Ensure each result has a unique id
      const resultsWithIds = (response.data || []).map((result, index) => ({
        ...result,
        id: result.id || `result-${Date.now()}-${index}`
      }));
      
      // Clear selections on new search
      setSelectedResults([]);
      setResults(resultsWithIds);
      toast.success(`Se encontraron ${resultsWithIds.length} resultados`);
    } catch (error) {
      console.error('Error en búsqueda:', error);
      setResults([]); // Clear results on error
      toast.error(error.response?.data?.detail || 'Error en la búsqueda');
    } finally {
      setSearching(false);
    }
  };

  const handleAdmit = (result) => {
    setSelectedResult(result);
    setSelectedCategory('');
    setShowAdmitModal(true);
  };

  const confirmAdmit = async () => {
    if (!selectedCategory) {
      toast.error('Debe seleccionar una categoría');
      return;
    }

    // Si tenemos la función onAdmitResult, navegamos al formulario con datos prellenados
    if (onAdmitResult) {
      const datosPrellenos = {
        tipo_gestion: 'Monitoreo de Medios',
        descripcion: selectedResult.summary || '',
        fecha_asignacion: selectedResult.date ? selectedResult.date.split('T')[0] : new Date().toISOString().split('T')[0],
        campos_especificos: {
          author: selectedResult.author || '',
          network: selectedResult.network || 'X',
          link: selectedResult.link || '',
          category: selectedCategory,
          persons: '',
          Estado_Actual: 'Registrado'
        },
        scraping_result_id: selectedResult.id
      };
      
      setShowAdmitModal(false);
      setSelectedResult(null);
      onAdmitResult(datosPrellenos);
      return;
    }

    // Comportamiento por defecto: crear automáticamente (legacy)
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/scraping/admit/${selectedResult.id}`,
        { category: selectedCategory },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Resultado admitido y publicación creada');
      setShowAdmitModal(false);
      setSelectedResult(null);
      fetchResults();
    } catch (error) {
      console.error('Error al admitir:', error);
      toast.error(error.response?.data?.detail || 'Error al admitir resultado');
    }
  };

  const handleReject = async (resultId) => {
    if (!window.confirm('¿Está seguro de rechazar este resultado?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/scraping/reject/${resultId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Resultado rechazado');
      fetchResults();
    } catch (error) {
      console.error('Error al rechazar:', error);
      toast.error(error.response?.data?.detail || 'Error al rechazar resultado');
    }
  };

  const handleToggleSelect = (resultId) => {
    console.log('Toggle select:', resultId);
    setSelectedResults(prev => {
      const newSelection = prev.includes(resultId) 
        ? prev.filter(id => id !== resultId)
        : [...prev, resultId];
      console.log('New selection:', newSelection);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    const pendingResults = results.filter(r => r.status === 'pending');
    if (selectedResults.length === pendingResults.length) {
      setSelectedResults([]);
    } else {
      setSelectedResults(pendingResults.map(r => r.id));
    }
  };

  const handleBulkReject = async () => {
    if (selectedResults.length === 0) {
      toast.error('Debe seleccionar al menos un resultado');
      return;
    }

    if (!window.confirm(`¿Está seguro de rechazar ${selectedResults.length} resultado(s)?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let success = 0;
      let errors = 0;

      for (const resultId of selectedResults) {
        try {
          await axios.post(
            `${API}/scraping/reject/${resultId}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          success++;
        } catch (error) {
          console.error(`Error al rechazar ${resultId}:`, error);
          errors++;
        }
      }

      if (success > 0) {
        toast.success(`${success} resultado(s) rechazado(s)`);
      }
      if (errors > 0) {
        toast.error(`${errors} error(es) al rechazar`);
      }

      setSelectedResults([]);
      fetchResults();
    } catch (error) {
      console.error('Error en rechazo masivo:', error);
      toast.error('Error en rechazo masivo');
    }
  };

  const handleExportExcel = () => {
    if (results.length === 0) {
      toast.error('No hay resultados para exportar');
      return;
    }

    const exportData = results.map(result => ({
      'Fecha': new Date(result.date).toLocaleDateString('es-ES'),
      'Red Social': result.network,
      'Autor': result.author,
      'Sentimiento': result.sentiment,
      'Resumen': result.summary,
      'Enlace': result.link,
      'Relevancia': result.relevance || 'Pendiente',
      'Estado': result.status === 'pending' ? 'Pendiente' : result.status === 'admitted' ? 'Admitido' : 'Rechazado'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Resultados');
    
    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `web_scraping_${date}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  const getSentimentBadgeClass = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'negativo': return 'sentiment-negative';
      case 'positivo': return 'sentiment-positive';
      case 'neutral': return 'sentiment-neutral';
      default: return 'sentiment-neutral';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'negativo': return <ThumbsDown size={14} />;
      case 'positivo': return <ThumbsUp size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'admitted': return 'status-admitted';
      case 'rejected': return 'status-rejected';
      default: return 'status-pending';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'admitted': return 'Admitido';
      case 'rejected': return 'Rechazado';
      default: return status;
    }
  };

  return (
    <div className="scraping-container fade-in" data-testid="scraping-view">
      <div className="container">
        {/* Header */}
        <div className="panel">
          <div className="scraping-header">
            <div>
              <h2 className="panel-title">
                <TrendingUp size={24} />
                Web Scraping - Detección Automática
              </h2>
              <p className="scraping-subtitle">
                Identifica publicaciones relevantes sobre corrupción en redes sociales
              </p>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        <div className="panel">
          <div className="filters-header">
            <Filter size={20} />
            <h3>Filtros de Búsqueda</h3>
            <span className="required-note">* Campos obligatorios</span>
          </div>

          <div className="filters-grid">
            <div className="form-group">
              <label>
                <Calendar size={16} />
                Fecha Desde *
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                max={new Date().toISOString().split('T')[0]}
                required
                data-testid="date-from"
              />
            </div>

            <div className="form-group">
              <label>
                <Calendar size={16} />
                Fecha Hasta *
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                max={new Date().toISOString().split('T')[0]}
                required
                data-testid="date-to"
              />
              <small className="helper-text">Máximo 5 días de diferencia</small>
            </div>

            <div className="form-group">
              <label>
                <Hash size={16} />
                Red Social
              </label>
              <select
                value={filters.network}
                onChange={(e) => setFilters({...filters, network: e.target.value})}
                data-testid="network-filter"
              >
                <option value="">Todas</option>
                {redesSociales.length > 0 ? (
                  redesSociales.map(red => (
                    <option key={red.id} value={red.value}>{red.value}</option>
                  ))
                ) : (
                  <>
                    <option value="X">X</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="TikTok">TikTok</option>
                    <option value="YouTube">YouTube</option>
                  </>
                )}
              </select>
            </div>

            <div className="form-group">
              <label>
                <User size={16} />
                Autor / Cuenta
              </label>
              <select
                value={cuentasSociales.some(c => c.value === filters.author) ? filters.author : (filters.author ? '__otro__' : '')}
                onChange={(e) => {
                  if (e.target.value === '__otro__') {
                    setFilters({...filters, author: ''});
                  } else {
                    setFilters({...filters, author: e.target.value});
                  }
                }}
                data-testid="author-select"
              >
                <option value="">Seleccione una cuenta</option>
                {cuentasSociales.map(cuenta => (
                  <option key={cuenta.id} value={cuenta.value}>{cuenta.value} {cuenta.red_social ? `(${cuenta.red_social})` : ''}</option>
                ))}
                <option value="__otro__">-- Otra cuenta --</option>
              </select>
              {(!cuentasSociales.some(c => c.value === filters.author) || filters.author === '') && (
                <input
                  type="text"
                  value={cuentasSociales.some(c => c.value === filters.author) ? '' : filters.author}
                  onChange={(e) => setFilters({...filters, author: e.target.value})}
                  placeholder="@armandocastGT"
                  style={{ marginTop: '0.5rem' }}
                  data-testid="author-filter"
                />
              )}
              <small className="helper-text">Ejemplo: @armandocastGT (opcional)</small>
            </div>

            <div className="form-group">
              <label>
                <User size={16} />
                Persona Mencionada
              </label>
              <input
                type="text"
                value={filters.person}
                onChange={(e) => setFilters({...filters, person: e.target.value})}
                placeholder="Nombre de persona"
                data-testid="person-filter"
              />
            </div>

            <div className="form-group">
              <label>Sentimiento</label>
              <select
                value={filters.sentiment}
                onChange={(e) => setFilters({...filters, sentiment: e.target.value})}
                data-testid="sentiment-filter"
              >
                <option value="">Todos</option>
                <option value="negativo">Negativo</option>
                <option value="positivo">Positivo</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
          </div>

          <div className="filters-actions">
            <button
              className="btn btn-primary"
              onClick={handleSearch}
              disabled={searching}
              data-testid="search-btn"
            >
              {searching ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Buscando...
                </>
              ) : (
                <>
                  <Search size={16} />
                  Buscar Publicaciones
                </>
              )}
            </button>
          </div>
        </div>

        {/* Results Panel */}
        <div className="panel">
          <div className="results-header">
            <h3>Resultados de Búsqueda ({results.length})</h3>
            <div className="results-actions">
              {selectedResults.length > 0 && canEdit() && (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleBulkReject}
                  data-testid="bulk-reject-btn"
                >
                  <X size={16} />
                  Rechazar Seleccionados ({selectedResults.length})
                </button>
              )}
              <button
                className="btn btn-success btn-sm"
                onClick={handleExportExcel}
                disabled={results.length === 0}
                data-testid="export-excel-btn"
              >
                <Download size={16} />
                Exportar Excel
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={fetchResults}
                disabled={loading}
                data-testid="refresh-results"
              >
                <RefreshCw size={16} />
                Actualizar
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loader"></div>
              <p>Cargando resultados...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="empty-state-container">
              <AlertCircle size={48} />
              <p>No hay resultados. Use los filtros para buscar publicaciones.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="scraping-table" data-testid="results-table">
                <thead>
                  <tr>
                    <th style={{width: '40px'}}>
                      <input
                        type="checkbox"
                        checked={selectedResults.length > 0 && selectedResults.length === results.filter(r => r.status === 'pending').length}
                        onChange={handleSelectAll}
                        disabled={!userCanEdit || results.filter(r => r.status === 'pending').length === 0}
                        title="Seleccionar todos"
                        style={{visibility: userCanEdit ? 'visible' : 'hidden'}}
                      />
                    </th>
                    <th>Fecha</th>
                    <th>Red Social</th>
                    <th>Autor</th>
                    <th>Sentimiento</th>
                    <th>Resumen</th>
                    <th>Relevancia</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result) => (
                    <tr key={result.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedResults.includes(result.id)}
                          onChange={() => handleToggleSelect(result.id)}
                          disabled={!userCanEdit || result.status !== 'pending'}
                          style={{visibility: userCanEdit ? 'visible' : 'hidden'}}
                        />
                      </td>
                      <td className="date-cell">
                        {new Date(result.date).toLocaleDateString('es-ES')}
                      </td>
                      <td>
                        <span className="network-badge">{result.network}</span>
                      </td>
                      <td className="author-cell">{result.author}</td>
                      <td>
                        <span className={`sentiment-badge ${getSentimentBadgeClass(result.sentiment)}`}>
                          {getSentimentIcon(result.sentiment)}
                          {result.sentiment}
                        </span>
                      </td>
                      <td className="summary-cell">{result.summary}</td>
                      <td>
                        <span className="relevance-badge">{result.relevance || 'Pendiente'}</span>
                      </td>
                      <td>
                        <span className={`status-badge-scraping ${getStatusBadgeClass(result.status)}`}>
                          {getStatusLabel(result.status)}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <a
                          href={result.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-icon"
                          title="Ver publicación"
                        >
                          <Eye size={16} />
                        </a>
                        {result.status === 'pending' && canEdit() && (
                          <>
                            <button
                              className="btn-icon btn-icon-success"
                              onClick={() => handleAdmit(result)}
                              title="Admitir"
                              data-testid={`admit-${result.id}`}
                            >
                              <Check size={16} />
                            </button>
                            <button
                              className="btn-icon btn-icon-danger"
                              onClick={() => handleReject(result.id)}
                              title="Rechazar"
                              data-testid={`reject-${result.id}`}
                            >
                              <X size={16} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Instructions Panel */}
        <div className="panel instructions-panel">
          <h3>
            <AlertCircle size={20} />
            Instrucciones de Uso
          </h3>
          <ol className="instructions-list">
            <li>
              <strong>Filtros obligatorios:</strong> Debe seleccionar un rango de fechas (máximo 5 días) y especificar una cuenta de usuario (ejemplo: @armandocastGT)
            </li>
            <li>
              <strong>Filtros opcionales:</strong> Red social, persona mencionada y sentimiento
            </li>
            <li>
              <strong>Búsqueda:</strong> El sistema busca publicaciones relacionadas con corrupción en Guatemala
            </li>
            <li>
              <strong>Análisis:</strong> Cada resultado incluye análisis automático de sentimiento
            </li>
            <li>
              <strong>Relevancia:</strong> Puede asignar manualmente el nivel de relevancia al revisar
            </li>
            <li>
              <strong>Admitir:</strong> Crea una publicación en Detalle con estado Registrado y la registra en bitácora
            </li>
            <li>
              <strong>Rechazar:</strong> Marca el resultado como rechazado (se conserva temporalmente para auditoría)
            </li>
          </ol>
        </div>
      </div>

      {/* Admit Modal */}
      {showAdmitModal && (
        <div className="modal-overlay" onClick={() => setShowAdmitModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Check size={20} />
                Admitir Resultado
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowAdmitModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                Seleccione la categoría para la publicación que se creará:
              </p>

              <div className="form-group">
                <label>Categoría *</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  data-testid="category-select"
                >
                  <option value="">Seleccione una categoría</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.value}>
                      {cat.value}
                    </option>
                  ))}
                </select>
              </div>

              {selectedResult && (
                <div className="result-preview">
                  <h4>Vista Previa</h4>
                  <p><strong>Autor:</strong> {selectedResult.author}</p>
                  <p><strong>Red:</strong> {selectedResult.network}</p>
                  <p><strong>Resumen:</strong> {selectedResult.summary}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAdmitModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn btn-success"
                onClick={confirmAdmit}
                disabled={!selectedCategory}
                data-testid="confirm-admit"
              >
                <Check size={16} />
                Admitir y Crear Publicación
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebScraping;
